import { useCallback, useEffect, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { GridCommandBarPreference } from '@solum/ui'
import { useSolum } from '../data/client'

/**
 * Per-user grid presentation that is not part of a saved view's meaning.
 *
 * Column order, filters and sort belong to `core.view`: collaborators opening
 * the same view must agree on those. Wrapping is like column width: a reading
 * preference for this person and entity. It therefore uses the existing
 * `core.user_setting` round trip and follows `useColumnWidths`' proven shape.
 * Density stays the design system's global user preference; ListView merely
 * exposes that existing control beside wrapping.
 */
const KEY = 'grid.presentation'
const SAVE_DEBOUNCE_MS = 300

export interface GridPresentationSettings {
  wrap?: boolean
  /**
   * The person's own command-button choice, or ABSENT — which is inheritance
   * from the governed table presentation (ADR F-84 §3). A stored legacy
   * `auto` MEANT inheritance, so it reads as absent (core 0.403.0 rewrites
   * stored ones the same way) and no save ever writes it back.
   */
  commandBar?: GridCommandBarPreference
  /** Ordered stable ids. Bounded because this is presentation, not a shadow table. */
  pinnedRows?: string[]
  /**
   * Columns pinned left (#2375 §6.3 "Pin column left"), in pin order — the
   * person's layout, beside their pinned rows. Column KEYS, bounded.
   */
  pinnedColumns?: string[]
}

type PresentationMap = Record<string, GridPresentationSettings>

const MAX_ROW_PINS = 100
const MAX_COLUMN_PINS = 50
const COMMAND_BAR_PREFERENCES = new Set<GridCommandBarPreference>(['shown', 'hidden'])

/** The sanitized setting: wrap and pins always present, the command bar only when CHOSEN. */
export type SanitizedGridPresentation = Required<Omit<GridPresentationSettings, 'commandBar'>>
  & Pick<GridPresentationSettings, 'commandBar'>

export function sanitizeGridPresentation(value: unknown): SanitizedGridPresentation {
  const row = value && typeof value === 'object' ? value as Record<string, unknown> : {}
  const pins = Array.isArray(row.pinnedRows) ? row.pinnedRows : []
  const columnPins = Array.isArray(row.pinnedColumns) ? row.pinnedColumns : []
  return {
    wrap: row.wrap === true,
    /* `auto` (legacy) and anything unknown read as INHERIT — absent. */
    ...(COMMAND_BAR_PREFERENCES.has(row.commandBar as GridCommandBarPreference)
      ? { commandBar: row.commandBar as GridCommandBarPreference } : {}),
    pinnedRows: [...new Set(pins.filter((id): id is string => typeof id === 'string' && id.length > 0))]
      .slice(0, MAX_ROW_PINS),
    pinnedColumns: [...new Set(columnPins.filter((key): key is string => typeof key === 'string' && key.length > 0))]
      .slice(0, MAX_COLUMN_PINS),
  }
}

/** What the person SEES: their own choice, else the governed default. */
export function effectiveCommandBar(
  personal: GridCommandBarPreference | undefined,
  governed: GridCommandBarPreference,
): GridCommandBarPreference {
  return personal ?? governed
}

export interface GridPresentation {
  wrap: boolean
  setWrap: (wrap: boolean) => void
  /** Absent ⇒ inherit the governed default. */
  commandBar: GridCommandBarPreference | undefined
  /** `undefined` returns the person to inheritance. */
  setCommandBar: (preference: GridCommandBarPreference | undefined) => void
  pinnedRows: string[]
  setPinnedRows: (ids: string[]) => void
  pinnedColumns: string[]
  setPinnedColumns: (keys: string[]) => void
  ready: boolean
}

export function useGridPresentation(entity: string): GridPresentation {
  const { client, isLive } = useSolum()
  const qc = useQueryClient()
  const query = useQuery({
    queryKey: ['user-settings'],
    queryFn: () => client.raw().rpcScalar<Record<string, unknown>>('my_settings'),
    enabled: isLive,
    staleTime: 60_000,
  })

  const settled = !query.isLoading || query.isError
  const stored = ((query.data?.[KEY] ?? {}) as PresentationMap)[entity]
  const [presentation, setPresentation] = useState<SanitizedGridPresentation>(() => sanitizeGridPresentation(null))
  const adoptedEntity = useRef<string | null>(null)
  useEffect(() => {
    if (!settled || adoptedEntity.current === entity) return
    adoptedEntity.current = entity
    setPresentation(sanitizeGridPresentation(stored))
  }, [entity, settled, stored])

  const save = useMutation({
    mutationFn: (next: SanitizedGridPresentation) => {
      const all = (query.data?.[KEY] ?? {}) as PresentationMap
      return client.raw().rpcScalar('set_user_setting', {
        p_key: KEY,
        p_value: { ...all, [entity]: next },
      })
    },
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['user-settings'] }) },
  })

  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  useEffect(() => () => { if (timer.current) clearTimeout(timer.current) }, [])

  const update = useCallback((change: Partial<SanitizedGridPresentation>) => {
    const merged: Record<string, unknown> = { ...presentation, ...change }
    if ('commandBar' in change && change.commandBar === undefined) delete merged.commandBar
    const next = sanitizeGridPresentation(merged)
    setPresentation(next)
    if (!isLive) return
    if (timer.current) clearTimeout(timer.current)
    timer.current = setTimeout(() => save.mutate(next), SAVE_DEBOUNCE_MS)
  }, [isLive, presentation, save])

  return {
    ...presentation,
    commandBar: presentation.commandBar,
    setWrap: (wrap) => update({ wrap }),
    setCommandBar: (commandBar) => update({ commandBar }),
    setPinnedRows: (pinnedRows) => update({ pinnedRows }),
    setPinnedColumns: (pinnedColumns) => update({ pinnedColumns }),
    ready: settled,
  }
}
